import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/* BUG-3: a little longer than --submenu-animation-speed (360ms in base.css),
   so the panel has finished revealing before a pointer leave can close it. */
const DEACTIVATE_GRACE_MS = 400;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {IntersectionObserver | null}
   */
  #menuVideoObserver = null;

  /**
   * Pending close after the pointer leaves a menu item (BUG-3).
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimer = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.#observeMenuVideos();
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    /* WCAG 2.1.1 (Batch 3, fix #14): self-wire focusin/focusout on the
       host as a defensive layer so keyboard mega-menu access survives
       even if a future Liquid template edit drops the `on:focus="/activate"`
       / `on:blur="/deactivate"` declarative bindings on individual <li>
       items. focusin/focusout bubble through shadow boundaries, so a
       single host-level listener covers every nested menu item. Pointer
       hover handling stays template-driven because pointerenter /
       pointerleave do not bubble. The activate() / deactivate() methods
       already accept FocusEvent and dedupe via activeItem state, so a
       second event path from bubbling is a no-op rather than a regression. */
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    /* WCAG 1.4.13 (audit N-04): hover-opened panels must be
       dismissible without moving the pointer. Document-level so
       Escape works while the pointer rests on the trigger/panel and
       focus sits elsewhere. */
    document.addEventListener('keydown', this.#handleEscape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('keydown', this.#handleEscape);
    this.#cancelScheduledDeactivate();
    this.#cleanupMutationObserver();
    this.#menuVideoObserver?.disconnect();
    this.#menuVideoObserver = null;
  }

  /**
   * Escape closes any open hover panel (WCAG 1.4.13, audit N-04).
   * If focus is inside the open item, it returns to the trigger so
   * keyboard users are not stranded in a closed panel.
   * @param {KeyboardEvent} event
   */
  #handleEscape = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;
    if (item.contains(document.activeElement)) {
      const trigger = item.querySelector('a, button');
      if (trigger instanceof HTMLElement) trigger.focus();
    }
    this.#deactivate();
  };

  /**
   * Forward bubbling focusin/focusout to the same handlers the Liquid
   * `on:focus` / `on:blur` bindings target. See connectedCallback comment.
   * @param {FocusEvent} event
   */
  #handleFocusIn = (event) => {
    this.activate(event);
  };
  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    this.deactivate(event);
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * The "More" disclosure button that reveals the overflow panel.
   * @returns {HTMLButtonElement | null}
   */
  get overflowToggle() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[slot="more"] button'));
  }

  /**
   * Record whether the overflow panel is open, and mirror that state on the
   * "More" button so assistive technology hears expanded or collapsed.
   * @param {boolean} expanded
   */
  #setOverflowExpanded(expanded) {
    const value = expanded.toString();
    this.dataset.overflowExpanded = value;
    this.overflowToggle?.setAttribute('aria-expanded', value);
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#cancelScheduledDeactivate();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.#setOverflowExpanded(!isDefaultSlot);

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    /* BUG-3 (2026-10-30): the panel reveals with a clip-path transition
       (--submenu-animation-speed, 360ms) and clip-path also limits
       hit-testing. While it is still growing, the area below the menu row
       belongs to .header__underlay-open, outside the <li>, so a mouse moving
       down to a link fired pointerleave and the panel shut before it could
       be clicked. Mouse/pen leaves now close after a short grace period
       instead; keyboard (blur/focusout), Escape and touch stay immediate. */
    if (event instanceof PointerEvent && event.type === 'pointerleave' && event.pointerType !== 'touch') {
      this.#scheduleDeactivate(event);
      return;
    }

    this.#deactivate();
  }

  /**
   * Last known pointer position while a close is pending (BUG-3).
   * @type {{ x: number, y: number }}
   */
  #pendingPointer = { x: 0, y: 0 };

  /** @param {PointerEvent} event */
  #trackPendingPointer = (event) => {
    this.#pendingPointer = { x: event.clientX, y: event.clientY };
  };

  /**
   * Close the active item once the pointer has really left it (BUG-3).
   * By the time the grace period ends the panel has finished revealing, so
   * if the pointer now sits over the item or its submenu (both inside the
   * <li>) the panel stays open; otherwise it closes. Hit-tests the last
   * pointer position rather than :hover, because :hover is not refreshed
   * for a resting pointer when the panel grows underneath it.
   * @param {PointerEvent} event
   */
  #scheduleDeactivate(event) {
    this.#cancelScheduledDeactivate();
    const item = this.#state.activeItem;
    if (!item) return;

    this.#pendingPointer = { x: event.clientX, y: event.clientY };
    document.addEventListener('pointermove', this.#trackPendingPointer, { passive: true });

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateTimer = null;
      document.removeEventListener('pointermove', this.#trackPendingPointer);
      if (item != this.#state.activeItem) return;

      const { x, y } = this.#pendingPointer;
      const elementUnderPointer = document.elementFromPoint(x, y);
      if (elementUnderPointer && item.parentElement?.contains(elementUnderPointer)) return;

      this.#deactivate(item);
    }, DEACTIVATE_GRACE_MS);
  }

  #cancelScheduledDeactivate() {
    if (this.#deactivateTimer !== null) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = null;
    }
    document.removeEventListener('pointermove', this.#trackPendingPointer);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#cancelScheduledDeactivate();
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.#setOverflowExpanded(false);

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   *
   * If the sizes attribute leads with 'auto' (only valid while lazy), strip it
   * before removing loading — otherwise the whole sizes list is invalid, the
   * browser assumes 100vw, and this "preload" fetches the largest srcset
   * candidate. See the matching fix in product-card.js, where this exact
   * pattern was measured pulling a 3840w original into a 228px slot.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => {
      const sizes = image.getAttribute('sizes');
      if (sizes && sizes.trim().startsWith('auto')) {
        const stripped = sizes.replace(/^\s*auto\s*,?\s*/, '');
        const fallback = `${Math.max(image.parentElement?.clientWidth || 0, 240)}px`;
        image.setAttribute('sizes', stripped || fallback);
      }
      image.removeAttribute('loading');
    });
  };

  /**
   * Starts mega-menu chapter videos only once their panel is actually
   * visible (2026-08-04 audit). They ship preload="none" without the native
   * autoplay attribute, because autoplay was downloading 12+ MB of video on
   * every page load while the menu was still closed. A closed panel never
   * intersects, so this costs nothing until the visitor opens the menu.
   * Playback respects prefers-reduced-motion; the poster still shows.
   */
  #observeMenuVideos() {
    const videos = this.querySelectorAll('video[data-mega-menu-nh-video]');
    if (videos.length === 0) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.#menuVideoObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (!reducedMotion.matches && entry.target instanceof HTMLVideoElement) {
          const maybePromise = entry.target.play();
          if (maybePromise && typeof maybePromise.catch === 'function') {
            maybePromise.catch(() => {});
          }
        }
        this.#menuVideoObserver?.unobserve(entry.target);
      }
    });
    videos.forEach((video) => this.#menuVideoObserver?.observe(video));
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 *
 * Two call paths feed this:
 *   - Template-driven `on:focus="/activate"` declarative bindings on each
 *     <li> — event.target is the <li>, the inner [ref="menuitem"] is a
 *     descendant.
 *   - WCAG 2.1.1 (Batch 3, fix #14) self-wired host focusin listener —
 *     event.target is the actual focused button/anchor, which IS the
 *     menuitem, so the descendant lookup would return null.
 * Handle both shapes so the host-level defensive listener stays useful.
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // Element itself is the menuitem (self-wired focusin path).
  if (element.matches('[ref="menuitem"]')) {
    return element instanceof HTMLElement ? element : null;
  }

  return /** @type {HTMLElement | null} */ (element?.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
